/**
 * @fileoverview ShoppingItem Component
 *
 * Individual shopping cart item component that displays item details and controls.
 * Features include:
 * - Item image with fallback placeholder
 * - Item information (name, code, description, price)
 * - Quantity controls (increment/decrement buttons)
 * - Optional remove button (currently commented out)
 * - Readonly mode for checkout review
 */

import React from "react";
import { IoMdTrash } from "react-icons/io";
import { MdOutlinePhoto } from "react-icons/md";
import { ShoppingItemProps } from "./ShoppingItem.types";
import { SHOP_CONFIG } from "pages/Shop/ShopList.config";
import "./ShoppingItem.scss";

/**
 * ShoppingItem Component
 *
 * Renders an individual cart item with its image, details, and interaction controls.
 * Supports both interactive mode (with quantity controls) and readonly mode
 * for different contexts like cart editing vs checkout review.
 *
 * @param props - Component props including item data and event handlers
 * @returns JSX element representing a single cart item
 */
const ShoppingItem: React.FC<ShoppingItemProps> = ({
  item,
  onUpdateQuantity,
  onRemoveItem,
  readonly = false,
}) => {
  /**
   * Handles increasing item quantity by 1
   */
  const handleIncrement = () => {
    onUpdateQuantity(item.id, item.quantity + 1);
  };

  /**
   * Handles decreasing item quantity by 1
   * Prevents quantity from going below 0
   */
  const handleDecrement = () => {
    if (item.quantity > 0) {
      onUpdateQuantity(item.id, item.quantity - 1);
    }
  };

  /**
   * Handles removing the item from cart entirely
   */
  const handleRemove = () => {
    onRemoveItem(item.id);
  };

  // Check if item has a valid image URL
  const hasValidImage = item.itemImageUrl && item.itemImageUrl.trim() !== "";

  // Inline styles for better maintainability
  const imageStyles: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "4px",
  };

  const placeholderStyles: React.CSSProperties = {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#cccccc",
  };

  return (
    <div className="ShoppingItem" data-testid="cart-item">
      {/* Item Image Section */}
      <div className="ItemImage" data-testid="cart-item-image">
        {hasValidImage ? (
          <img src={item.itemImageUrl} alt={item.name} style={imageStyles} />
        ) : (
          // Fallback placeholder when no image is available
          <div style={placeholderStyles}>{MdOutlinePhoto({ size: "60%" })}</div>
        )}
      </div>

      {/* Item Details Section */}
      <div className="ItemDetails">
        <h3 className="ItemName" data-testid="cart-item-name">
          {item.name}
        </h3>
        <p className="ItemCode" data-testid="cart-item-code">
          Item code
          <br />
          {item.itemCode}
        </p>
        <p className="ItemDescription" data-testid="cart-item-desc">
          {item.itemDescription}
        </p>
        <div className="PriceSection">
          {/* Show quantity in readonly mode, controls in interactive mode */}
          {readonly && <span className="ItemQuantity">Quantity: {item.quantity}</span>}
          <p className="ItemPrice" data-testid="cart-item-price">
            {item.currencySymbol || SHOP_CONFIG.FALLBACK_CURRENCY_SYMBOL}
            {item.price.toFixed(2)} each
          </p>
        </div>
      </div>

      {/* Item Controls Section - Only shown in interactive mode */}
      {!readonly && (
        <div className="ItemControls">
          <div className="QuantityControls">
            <button
              className="QuantityButton"
              data-testid="cart-item-qty-dec"
              onClick={handleDecrement}
              disabled={item.quantity <= 0}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className="QuantityDisplay" data-testid="cart-item-qty">
              {item.quantity}
            </span>
            <button
              className="QuantityButton"
              data-testid="cart-item-qty-inc"
              onClick={handleIncrement}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
          {/* Remove button currently disabled - uncomment to enable */}
          {/* <button className="RemoveButton" onClick={handleRemove} aria-label="Remove from basket">
            {IoMdTrash({ size: 16 })}
            Remove from basket
          </button> */}
        </div>
      )}
    </div>
  );
};

export default ShoppingItem;
